import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import { DOCUMENT_STATUS_LABELS, DOCUMENT_STATUSES, getDocumentGroupLabel } from '@utils/documentVault';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { SelectionCheckbox } from '../selection/SelectionCheckbox';
import { DocumentVaultSelectionContext } from '@/contexts/DocumentVaultSelectionContext';
import { CellWrapper, RowWrapper } from './styles';

const DetailsButtonWrapper = styled.div`
  button {
    padding: 6px 16px;
  }
`;

const StatusText = styled.span`
  * {
    color: ${({ theme, $status }) =>
      $status === DOCUMENT_STATUSES.failed
        ? theme.themeColors.error
        : $status === DOCUMENT_STATUSES.processing
        ? theme.themeColors.secondaryText
        : theme.themeColors.text};
  }
`;

/*
  A row click only ever selects. Opening, downloading, and tagging are deliberate choices behind
  the Details drawer, so a stray click on a list of documents cannot spend a clean copy or throw
  the reader into a viewer they did not ask for.
*/
export const VaultTableRow = ({ doc, status, density, onShowDetails }) => {
  const { isSelected, toggleDocument } = React.useContext(DocumentVaultSelectionContext);
  const { filename, doc_type, created_at } = doc;

  const uploadedAt = created_at ? formatLocalizedDateTime('ll2', createTimeInstance(created_at)) : null;

  /*
    The row is a click target, not a control: the checkbox inside it stays the labeled, focusable
    thing, so keyboard users get one checkbox per row rather than a button wrapping a checkbox
    that both claim the same name.
  */
  return (
    <RowWrapper $density={density} $isSelected={isSelected(doc)} onClick={() => toggleDocument(doc)}>
      <CellWrapper>
        <SelectionCheckbox
          isChecked={isSelected(doc)}
          onChange={() => toggleDocument(doc)}
          ariaLabel={`Select ${filename}`}
        />
      </CellWrapper>
      <CellWrapper title={filename}>
        <Body5 truncate>{filename}</Body5>
      </CellWrapper>
      <CellWrapper>
        <Body5 isLowContrast truncate>
          {doc_type || '-'}
        </Body5>
      </CellWrapper>
      <CellWrapper>
        <Body5 isLowContrast truncate>
          {getDocumentGroupLabel(doc)}
        </Body5>
      </CellWrapper>
      <CellWrapper>
        <Body5 isLowContrast>{uploadedAt || '-'}</Body5>
      </CellWrapper>
      <CellWrapper>
        <StatusText $status={status}>
          <Body5>{DOCUMENT_STATUS_LABELS[status]}</Body5>
        </StatusText>
      </CellWrapper>
      {/* Details is its own action, so it must not also toggle the row it sits in. */}
      <CellWrapper onClick={(event) => event.stopPropagation()}>
        <DetailsButtonWrapper>
          <SkeletonButton onClick={() => onShowDetails(doc)}>Details</SkeletonButton>
        </DetailsButtonWrapper>
      </CellWrapper>
    </RowWrapper>
  );
};
